import { SENSITIVE_QUERY_PARAMS } from "@/lib/salesforce/sanitize-page-url";
import { isPageContextEnabled } from "./pageContext";

/**
 * Cleans a page URL before it goes to Salesforce Data Cloud as `source.url`
 * (and `source.urlReferrer`). The SDK would otherwise send `location.href`
 * as is, query string and fragment included.
 *
 * - Every page: the fragment and any credentials are dropped, and so is any
 *   query param that is named like an email address (`email`, `e-mail`,
 *   `user_email`, `mail`, ...), is listed in SENSITIVE_QUERY_PARAMS (`aqt`,
 *   `sftoken`, `email`), or carries an email address as its value
 *   (`utm_content={{email}}`). Every other param keeps its exact original
 *   spelling.
 * - `/search` only: the query string is reduced to `q` and `page`. `q` is
 *   replaced by a fixed marker when it looks like a phone number, a card or
 *   social security number, or a number of nine or more digits. A term that
 *   holds one email address is reduced to its domain (`REDACTED@acme.com`), so
 *   the company searched for is kept and the person is not. Otherwise `q` is
 *   capped at 100 characters. Topic words, client names and short codes such as
 *   `NAICS 541511` go through unchanged.
 */

/** Stands in for a search term that looks like personal data. */
export const REDACTED_SEARCH_TERM = "REDACTED";

export const SEARCH_TERM_MAX_LENGTH = 100;

/** The most of a value that is scanned for personal data, so a huge `q` cannot stall the page. */
const MAX_SCAN_LENGTH = 500;

const SEARCH_PATH = /^\/search\/?$/;
const PAGE_NUMBER = /^\d{1,4}$/;

/** An `@` with something on both sides, spaces around it allowed: jane@example.com, jane @example.com */
const EMAIL_ADDRESS = /\S\s?@\s?\S/;
/**
 * Nine or more digits in a row: phone numbers typed without separators, card and
 * social security numbers, account and ID numbers. Shorter runs (NAICS and SIC
 * codes, ZIP codes, years) are searches worth keeping.
 */
const LONG_DIGIT_RUN = /\p{Nd}{9,}/u;
/**
 * Ten or more digits that may be broken up by single spaces, dots, dashes or brackets:
 * (555) 123-4567, +44 20 7946 0958, 4111 1111 1111 1111.
 */
const SPACED_DIGITS = /(?:\p{Nd}[ .()-]{0,2}){10,}/u;
/** 123-45-6789 */
const SOCIAL_SECURITY_NUMBER = /(?:^|\D)\d{3}[- ]\d{2}[- ]\d{4}(?!\d)/;
/** A search for nothing but a seven-digit local number: 555-1234. (Inside a longer term it is usually a section or part number.) */
const LOCAL_NUMBER_ONLY = /^\+?\d{3}[-.\s]\d{4}$/;

function safeDecode(text: string): string {
    try {
        return decodeURIComponent(text);
    } catch {
        return text;
    }
}

/** Decodes the way a query string is written: `+` is a space, then percent escapes. */
function decodeQueryPart(text: string): string {
    return safeDecode(text.replace(/\+/g, " "));
}

/** What to scan for personal data: decoded once more (catches %2540), width-folded (catches full-width digits and @), bounded. */
function toScan(text: string): string {
    // Cut first (leaving room for percent escapes, and not mid-escape), so the cost never depends on how long the value is.
    const head = text.slice(0, MAX_SCAN_LENGTH * 3).replace(/%[0-9a-fA-F]?$/, "");
    return safeDecode(head).normalize("NFKC").slice(0, MAX_SCAN_LENGTH);
}

function looksLikeEmailAddress(text: string): boolean {
    return EMAIL_ADDRESS.test(toScan(text));
}

/** A phone, card, social security or long ID number: nothing of the term is kept. */
function looksLikeNumberToHide(scan: string): boolean {
    return (
        LONG_DIGIT_RUN.test(scan) ||
        SPACED_DIGITS.test(scan) ||
        SOCIAL_SECURITY_NUMBER.test(scan) ||
        LOCAL_NUMBER_ONLY.test(scan.trim())
    );
}

/** The longest domain kept, so `REDACTED@` plus the domain stays well under the term cap. */
const MAX_KEPT_DOMAIN_LENGTH = 80;
const EMAIL_DOMAIN = /@\s?([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+)/i;

/**
 * `REDACTED@acme.com` for a term that holds exactly one address with a real
 * domain, so a search for a person at a company still says which company. Two
 * addresses, a domain that is not one (`jane@localhost`) or an address buried
 * in something odd gets the plain marker.
 */
function redactEmailKeepingDomain(scan: string): string {
    if ((scan.match(/@/g) ?? []).length !== 1) return REDACTED_SEARCH_TERM;
    const domain = EMAIL_DOMAIN.exec(scan)?.[1]?.toLowerCase();
    if (!domain || domain.length > MAX_KEPT_DOMAIN_LENGTH || !/\.[a-z]{2,}$/.test(domain)) {
        return REDACTED_SEARCH_TERM;
    }
    return `${REDACTED_SEARCH_TERM}@${domain}`;
}

/**
 * Names that hold an address: `email`, `e-mail`, `user_email`, `contactEmail`,
 * `emailAddress`, `mail`, `eml`. Names that only mention email without holding
 * one (`email_campaign_id`, `utm_email_variant`) stay, and so does their
 * campaign attribution.
 */
function isSensitiveParamName(name: string): boolean {
    if (SENSITIVE_QUERY_PARAMS.includes(name.toLowerCase())) return true;
    const squashed = name.toLowerCase().replace(/[-_.\s]/g, "");
    return squashed === "mail" || squashed === "eml" || /email(?:address|addr)?$/.test(squashed);
}

/** One `name=value` piece of a raw query string. */
function isSensitivePair(pair: string): boolean {
    const equals = pair.indexOf("=");
    const name = decodeQueryPart(equals === -1 ? pair : pair.slice(0, equals));
    if (isSensitiveParamName(name)) return true;
    return equals !== -1 && looksLikeEmailAddress(decodeQueryPart(pair.slice(equals + 1)));
}

/**
 * The `q` value to report: fully redacted if it looks like a number to hide
 * (even next to an address), reduced to the domain of an email address, and
 * otherwise trimmed and capped.
 */
export function cleanSearchTerm(raw: string): string {
    const term = raw.trim();
    const scan = toScan(term);
    if (looksLikeNumberToHide(scan)) return REDACTED_SEARCH_TERM;
    if (EMAIL_ADDRESS.test(scan)) return redactEmailKeepingDomain(scan);
    return Array.from(term).slice(0, SEARCH_TERM_MAX_LENGTH).join("");
}

function withoutQueryOrFragment(rawUrl: string): string {
    return rawUrl.split("#")[0].split("?")[0];
}

/** `/search`, however it is spelled: `/search/`, `/Search`, `/%73earch`, `//search`. */
function isSearchPath(pathname: string): boolean {
    return SEARCH_PATH.test(safeDecode(pathname).toLowerCase().replace(/\/{2,}/g, "/"));
}

export function sanitizeTrackedUrl(rawUrl: string): string {
    let url: URL;
    try {
        url = new URL(rawUrl);
    } catch {
        // Not something we can reason about: report the part that cannot carry parameters.
        return withoutQueryOrFragment(rawUrl);
    }

    url.hash = "";
    url.username = "";
    url.password = "";

    if (isSearchPath(url.pathname)) {
        const kept = new URLSearchParams();
        const term = url.searchParams.get("q");
        if (term !== null && term.trim() !== "") kept.set("q", cleanSearchTerm(term));
        const page = url.searchParams.get("page");
        if (page !== null && PAGE_NUMBER.test(page)) kept.set("page", page);
        url.search = kept.toString();
        return url.toString();
    }

    // Filter the raw pairs rather than re-serialising the query, so every param that stays
    // keeps the exact spelling it arrived with (the same campaign URL is one URL in reports).
    const pairs = url.search.slice(1).split("&");
    const kept = pairs.filter((pair) => !isSensitivePair(pair));
    if (kept.length !== pairs.length) url.search = kept.join("&");
    return url.toString();
}

/**
 * A referrer from another site is reported as where it was, never what it
 * carried: origin and path, no query string or fragment. A referrer from this
 * site is cleaned like the page URL (a visitor can arrive from /search?q=...).
 */
export function sanitizeReferrer(rawReferrer: string, pageOrigin: string = window.location.origin): string {
    try {
        const url = new URL(rawReferrer);
        if (url.origin === pageOrigin) return sanitizeTrackedUrl(rawReferrer);
        return `${url.origin}${url.pathname}`;
    } catch {
        return "about:invalid-referrer";
    }
}

/**
 * The `source.url` and `source.urlReferrer` to send with a website event, or
 * nothing when NEXT_PUBLIC_SF_PAGE_CONTEXT is off (the SDK then fills in the
 * raw `location.href` and `document.referrer`, as it always has). The referrer
 * is only set when there is one, so the SDK's own default never takes over.
 */
export function trackedSourceUrls(): { url?: string; urlReferrer?: string } {
    if (!isPageContextEnabled()) return {};
    const urls: { url: string; urlReferrer?: string } = { url: sanitizeTrackedUrl(window.location.href) };
    if (document.referrer) urls.urlReferrer = sanitizeReferrer(document.referrer);
    return urls;
}
